import { AccountType } from '@synergy/models';
import { Box, Divider, Stack, styled } from '@mui/material';
import { PoolTypography } from '@synergy/components';
import { useNavigate } from 'react-router';

import { Organization } from '../../../common/models/organization';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import {
    contactMainOrganizationSelector,
    contactOrganizationsSelector,
    contactOverviewSelector,
} from '../../contacts-and-organizations/contact/contact-detail/store/contact-detail-global-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';

const StyledAnchor = styled('a')(({ theme }) => ({
    color: theme.palette.common.black,
    textDecoration: 'none',
    cursor: 'pointer',

    '&:hover': {
        color: theme.palette.primary.main,
    },
}));

const Information = ({ organization }: { organization: Organization }) => {
    const navigate = useNavigate();

    const handleOrganizationClick = () => {
        switch (organization.type) {
            case AccountType.Customer:
                navigate(ROUTES.contactsAndOrganizations.customers.detail.replace(':id', organization.id.toString()));
                return;
            case AccountType.Supplier:
                navigate(ROUTES.contactsAndOrganizations.suppliers.detail.replace(':id', organization.id.toString()));
                return;
            default:
                return;
        }
    };

    return (
        <Stack gap={0.5} key={organization.id}>
            <PoolTypography typography="body1" sx={{ fontWeight: 600 }}>
                <StyledAnchor onClick={() => handleOrganizationClick()}>{organization.name}</StyledAnchor>
            </PoolTypography>
            {organization.email && <InformationItem href={organization.email} type="email" />}
            {organization.website && <InformationItem href={organization.website} />}
            {organization.phone && <InformationItem href={organization.phone} type="phone" />}
        </Stack>
    );
};

const InformationItem = ({ href, type }: { href?: string; type?: 'email' | 'phone' }) => {
    if (!href) {
        return null;
    }

    const getHref = () => {
        let website = href;

        switch (type) {
            case 'email':
                return `mailto:${href}`;
            case 'phone':
                return `tel:${href}`;
            default:
                if (website && !href.match(/^[a-zA-Z]+:\/\//)) {
                    website = 'https://' + website;
                }
                return website;
        }
    };

    return (
        <PoolTypography typography="body1" component="span">
            <StyledAnchor href={getHref()} target={type ? '_self' : '_blank'}>
                {href}
            </StyledAnchor>
        </PoolTypography>
    );
};

export const DetailContactDetails = () => {
    const { contact } = useAppSelector(contactOverviewSelector);
    const organizations = useAppSelector(contactOrganizationsSelector);
    const mainOrganization = useAppSelector(contactMainOrganizationSelector);

    const otherOrganizations = organizations.filter(org => org.id !== contact?.mainAccount?.id);

    return (
        <Box typography="body2" p={4}>
            {mainOrganization && (
                <Stack gap={1} mb={4}>
                    <PoolTypography typography="body2" component="span" color="grey.700" id="app.label.mainAccount" />
                    <Information organization={mainOrganization} />
                </Stack>
            )}

            {otherOrganizations.length > 0 && (
                <>
                    <PoolTypography typography="body2" component="span" color="grey.700" id="app.label.otherAccounts" />
                    <Stack gap={2} divider={<Divider sx={{ color: theme => theme.palette.grey[300] }} />} mt={1}>
                        {otherOrganizations.map(org => (
                            <Information key={org.id} organization={org} />
                        ))}
                    </Stack>
                </>
            )}
        </Box>
    );
};
